/* eslint-disable max-lines */
import { createSlice } from "@reduxjs/toolkit";

/** @type {{ userId: string|null, profile: { relativeName: string, relationGroupId: string|null, relationTypeId: string|null, relativeAvatarId: string|null }, data: { occasionId: string, date: string, year?: string, occasionName?: string }[] }} */
const userReminderDataSelectionInitial = {
  userId: null,
  profile: {
    relativeName: "",
    relationGroupId: null,
    relationTypeId: null,
    relativeAvatarId: null,
  },
  data: [],
};

const initialState = {
  isLoading: false,
  error: null,
  allRelations: null,
  defaultOccasion: [],
  /** Normalized occasions from the dashboard `occasionChips` block for add-reminder drawer chips. */
  dashboardOccasionChips: [],
  userReminderDataSelection: {
    ...userReminderDataSelectionInitial,
    profile: { ...userReminderDataSelectionInitial.profile },
    data: [],
  },
};

const ReminderSlice = createSlice({
  name: "ReminderSlice",
  initialState,
  reducers: {
    responseToSetAllRelations: (state, action) => {
      return {
        ...state,
        allRelations: action.payload,
        isLoading: false,
      };
    },
    errorToSetAllRelations: (state, action) => {
      return {
        ...state,
        allRelations: null,
        isLoading: false,
        error: action.payload,
      };
    },
    /**
     * Appends one relation row to the group whose `id` / `groupId` matches `groupId`
     * (mutates nested `data` | `relations` | `items` in `allRelations` draft).
     */
    appendRelationToAllRelationsGroup: (state, action) => {
      const { groupId, item } = action.payload;
      if (!state.allRelations || groupId == null || groupId === "" || !item?.id) {
        return;
      }
      const raw = state.allRelations;
      /** @type {object[]|null} */
      let groups = null;
      if (Array.isArray(raw)) {
        groups = raw;
      } else if (raw && typeof raw === "object") {
        const nested = raw.data ?? raw.groups ?? raw.relationGroups ?? raw.result;
        groups = Array.isArray(nested) ? nested : null;
      }
      if (!groups || groups.length === 0) {
        return;
      }
      const gi = groups.findIndex((g) => g && String(g.id ?? g.groupId) === String(groupId));
      if (gi === -1) {
        return;
      }
      const group = groups[gi];
      const listKey = ["data", "relations", "items"].find((k) => Array.isArray(group[k]));
      if (!listKey) {
        return;
      }
      const label = item.label != null ? String(item.label) : "";
      group[listKey].push({
        id: String(item.id),
        label,
        name: label,
      });
    },
    resetUserReminderDataSelection: (state) => {
      return {
        ...state,
        userReminderDataSelection: {
          ...userReminderDataSelectionInitial,
          profile: { ...userReminderDataSelectionInitial.profile },
          data: [],
        },
      };
    },
    setUserReminderDataSelectionUserId: (state, action) => {
      return {
        ...state,
        userReminderDataSelection: {
          ...state.userReminderDataSelection,
          userId: action.payload != null && action.payload !== "" ? String(action.payload) : null,
        },
      };
    },
    setUserReminderSelectionProfile: (state, action) => {
      const patch = action.payload != null && typeof action.payload === "object" ? action.payload : {};
      const merged = {
        ...state.userReminderDataSelection.profile,
        ...patch,
      };
      delete merged.relativeAvatarUrl;
      return {
        ...state,
        userReminderDataSelection: {
          ...state.userReminderDataSelection,
          profile: merged,
        },
      };
    },
    clearUserReminderOccasionData: (state) => {
      return {
        ...state,
        userReminderDataSelection: {
          ...state.userReminderDataSelection,
          data: [],
        },
      };
    },
    /** Replaces `userReminderDataSelection.data` (e.g. profile edit sheet open from GET profile). */
    setUserReminderOccasionDataRows: (state, action) => {
      const rows = action.payload;
      if (!Array.isArray(rows)) {
        return state;
      }
      /** @type {{ occasionId: string, date: string, year?: string, occasionName?: string }[]} */
      const data = [];
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (row == null || typeof row !== "object") continue;
        const occasionId = row.occasionId != null ? String(row.occasionId).trim() : "";
        const date = row.date != null ? String(row.date).trim() : "";
        if (!occasionId || !date) continue;
        /** @type {{ occasionId: string, date: string, year?: string, occasionName?: string }} */
        const nextRow = { occasionId, date };
        if (row.year != null && String(row.year).trim() !== "") {
          nextRow.year = String(row.year).trim();
        }
        const nameRaw =
          [row.occasionName, row.name, row.title, row.label]
            .map((x) => (x != null ? String(x).trim() : ""))
            .find((s) => s !== "") ?? "";
        if (nameRaw) {
          nextRow.occasionName = nameRaw;
        }
        data.push(nextRow);
      }
      return {
        ...state,
        userReminderDataSelection: {
          ...state.userReminderDataSelection,
          data,
        },
      };
    },
    upsertUserReminderOccasionDate: (state, action) => {
      const { occasionId, date, year, occasionName } = action.payload;
      if (!occasionId || !date) {
        return state;
      }
      const prev = state.userReminderDataSelection.data;
      const idx = prev.findIndex((row) => String(row.occasionId) === String(occasionId));
      /** @type {{ occasionId: string, date: string, year?: string, occasionName?: string }} */
      const nextRow = { occasionId: String(occasionId), date: String(date) };
      if (year != null && year !== "") {
        nextRow.year = String(year);
      }
      if (occasionName != null && String(occasionName).trim() !== "") {
        nextRow.occasionName = String(occasionName).trim();
      }
      const data =
        idx === -1
          ? [...prev, nextRow]
          : prev.map((row, i) => {
              if (i !== idx) {
                return row;
              }
              const merged = { ...row, occasionId: nextRow.occasionId, date: nextRow.date };
              if (nextRow.year != null) {
                merged.year = nextRow.year;
              } else {
                delete merged.year;
              }
              if (nextRow.occasionName != null) {
                merged.occasionName = nextRow.occasionName;
              }
              return merged;
            });
      return {
        ...state,
        userReminderDataSelection: {
          ...state.userReminderDataSelection,
          data,
        },
      };
    },
    responseToSetDefaultOccasions: (state, action) => {
      return {
        ...state,
        defaultOccasion: action.payload,
      };
    },
    errorToSetDefaultOccasions: (state, action) => {
      return {
        ...state,
        defaultOccasion: [],
        error: action.payload,
      };
    },
    setDashboardOccasionChips: (state, action) => {
      return {
        ...state,
        dashboardOccasionChips: Array.isArray(action.payload) ? action.payload : [],
      };
    },
  },
});
export default ReminderSlice.reducer;
export const {
  responseToSetAllRelations,
  errorToSetAllRelations,
  appendRelationToAllRelationsGroup,
  resetUserReminderDataSelection,
  setUserReminderDataSelectionUserId,
  setUserReminderSelectionProfile,
  clearUserReminderOccasionData,
  setUserReminderOccasionDataRows,
  upsertUserReminderOccasionDate,
  responseToSetDefaultOccasions,
  errorToSetDefaultOccasions,
  setDashboardOccasionChips,
} = ReminderSlice.actions;
